/**
 * Colour maths for the design system: WCAG 2.1 contrast, alpha compositing, and
 * OKLab/OKLCH conversion.
 *
 * Two reasons this is a real module rather than a table of literals:
 *
 *   1. Legibility is *measured*, not asserted (design-language.md §5). Text over
 *      the generated background has to be checked against the worst case the art
 *      can produce, composited through whatever sits between — which is
 *      arithmetic, not a number anyone can eyeball.
 *   2. The web surface ramp is *derived* from the mobile palette in even steps of
 *      perceptual lightness. Committing the results as literals would let the
 *      stated derivation and the actual values drift apart; computing them means
 *      the documentation is executable.
 *
 * OKLab conversion follows Björn Ottosson's reference implementation.
 * No dependency — this is about sixty lines of well-specified arithmetic.
 */

export type Rgb = { r: number; g: number; b: number };
/** Lightness 0..1, chroma 0..~0.4, hue in degrees. */
export type Oklch = { l: number; c: number; h: number };

/** WCAG minimum for text below 24pt, or below 18.66pt when bold. */
export const AA_NORMAL = 4.5;
/** WCAG minimum for large text (≥24pt, or ≥18.66pt bold). */
export const AA_LARGE = 3;

export function hexToRgb(hex: string): Rgb {
  const h = hex.replace('#', '');
  const full =
    h.length === 3
      ? h
          .split('')
          .map((c) => c + c)
          .join('')
      : h;
  if (full.length !== 6) {
    throw new Error(`Expected a 3- or 6-digit hex colour, got "${hex}"`);
  }
  const n = Number.parseInt(full, 16);
  if (Number.isNaN(n)) {
    throw new Error(`"${hex}" is not a hex colour`);
  }
  return { r: (n >> 16) & 0xff, g: (n >> 8) & 0xff, b: n & 0xff };
}

export function rgbToHex({ r, g, b }: Rgb): string {
  const clamp = (v: number) => Math.min(255, Math.max(0, Math.round(v)));
  return `#${[r, g, b].map((v) => clamp(v).toString(16).padStart(2, '0')).join('')}`;
}

// ── WCAG ─────────────────────────────────────────────────────────────────────

function channelLuminance(value8Bit: number): number {
  const c = value8Bit / 255;
  return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

export function relativeLuminance(color: Rgb | string): number {
  const { r, g, b } = typeof color === 'string' ? hexToRgb(color) : color;
  return 0.2126 * channelLuminance(r) + 0.7152 * channelLuminance(g) + 0.0722 * channelLuminance(b);
}

export function contrastRatio(a: Rgb | string, b: Rgb | string): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const [hi, lo] = la >= lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

/**
 * Composite `over` onto `under` at the given alpha, in sRGB space — which is what
 * the compositor actually does, so it is what the eye actually sees.
 */
export function compositeOver(over: Rgb | string, under: Rgb | string, alpha: number): Rgb {
  const o = typeof over === 'string' ? hexToRgb(over) : over;
  const u = typeof under === 'string' ? hexToRgb(under) : under;
  const a = Math.min(Math.max(alpha, 0), 1);
  const mix = (x: number, y: number) => Math.round(y * a + x * (1 - a));
  return { r: mix(u.r, o.r), g: mix(u.g, o.g), b: mix(u.b, o.b) };
}

/**
 * Contrast of `text` against the worst case behind it: the darkest thing the
 * background art can produce, seen through `scrimAlpha` of the scrim colour.
 */
export function contrastOverScrim(
  text: string,
  worstCaseArt: string,
  scrimColor: string,
  scrimAlpha: number,
): number {
  // The scrim sits on top of the art, so the scrim is the `over` layer.
  return contrastRatio(text, compositeOver(scrimColor, worstCaseArt, scrimAlpha));
}

// ── OKLab / OKLCH ────────────────────────────────────────────────────────────

const toLinear = (v: number) => {
  const c = v / 255;
  return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
};

const fromLinear = (v: number) => {
  const c = v <= 0.0031308 ? 12.92 * v : 1.055 * v ** (1 / 2.4) - 0.055;
  return Math.min(255, Math.max(0, Math.round(c * 255)));
};

export function rgbToOklch(color: Rgb | string): Oklch {
  const { r, g, b } = typeof color === 'string' ? hexToRgb(color) : color;
  const lr = toLinear(r);
  const lg = toLinear(g);
  const lb = toLinear(b);

  const l = Math.cbrt(0.4122214708 * lr + 0.5363325363 * lg + 0.0514459929 * lb);
  const m = Math.cbrt(0.2119034982 * lr + 0.6806995451 * lg + 0.1073969566 * lb);
  const s = Math.cbrt(0.0883024619 * lr + 0.2817188376 * lg + 0.629978701 * lb);

  const L = 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s;
  const A = 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s;
  const B = 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s;

  const c = Math.hypot(A, B);
  const h = ((Math.atan2(B, A) * 180) / Math.PI + 360) % 360;
  return { l: L, c, h };
}

export function oklchToRgb({ l: L, c, h }: Oklch): Rgb {
  const rad = (h * Math.PI) / 180;
  const A = c * Math.cos(rad);
  const B = c * Math.sin(rad);

  const l_ = (L + 0.3963377774 * A + 0.2158037573 * B) ** 3;
  const m_ = (L - 0.1055613458 * A - 0.0638541728 * B) ** 3;
  const s_ = (L - 0.0894841775 * A - 1.291485548 * B) ** 3;

  return {
    r: fromLinear(4.0767416621 * l_ - 3.3077115913 * m_ + 0.2309699292 * s_),
    g: fromLinear(-1.2684380046 * l_ + 2.6097574011 * m_ - 0.3413193965 * s_),
    b: fromLinear(-0.0041960863 * l_ - 0.7034186147 * m_ + 1.707614701 * s_),
  };
}

/** Shift a colour's perceptual lightness, holding hue and chroma. */
export function adjustLightness(hex: string, delta: number): string {
  const { l, c, h } = rgbToOklch(hex);
  return rgbToHex(oklchToRgb({ l: Math.min(1, Math.max(0, l + delta)), c, h }));
}

/** Perceptual lightness, 0..1. The unit the surface ramp is stepped in. */
export function lightnessOf(hex: string): number {
  return rgbToOklch(hex).l;
}

/** `over` laid on `under` at `alpha`, returned as hex. */
export function mixOver(over: string, under: string, alpha: number): string {
  return rgbToHex(compositeOver(over, under, alpha));
}

/**
 * A token at partial opacity, as `rgba(...)`.
 *
 * For canvas. `ctx.fillStyle` cannot resolve custom properties — there is no
 * element to resolve them against — and assigning an unparseable string leaves
 * the PREVIOUS colour in place rather than throwing, so the drawing silently
 * comes out in whatever was set last. `var(--rt-accent)` and
 * `color-mix(in oklch, var(--rt-accent) 12%, transparent)` both fail this way.
 * Canvas code must pass concrete values; this produces them.
 */
export function withAlpha(hex: string, alpha: number): string {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${Math.min(1, Math.max(0, alpha))})`;
}
